import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Mail, Phone } from 'lucide-react';
import { FaInstagram, FaFacebookF, FaLinkedinIn, FaYoutube, FaTiktok, FaXTwitter, FaPinterest, FaSnapchat } from 'react-icons/fa6';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  instagram: FaInstagram,
  facebook: FaFacebookF,
  linkedin: FaLinkedinIn,
  youtube: FaYoutube,
  tiktok: FaTiktok,
  twitter: FaXTwitter,
  pinterest: FaPinterest,
  snapchat: FaSnapchat,
};

export function Footer() {
  const { t } = useLanguage();
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await axios.get(`${API}/social-links`);
        setSocialLinks(res.data.links || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchLinks();
  }, []);

  return (
    <footer data-testid="main-footer" className="border-t border-white/5 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-28 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-[#ED2227] rounded-sm flex items-center justify-center">
                <span className="text-white font-black text-sm font-[Chivo]">P4C</span>
              </div>
              <span className="text-white font-bold text-lg tracking-tight font-[Chivo]">
                PARTNERS4CARS
              </span>
            </div>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            {/* Company Logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/3o39rs6b_Partner4cars_logoL%20wit%20%281%29.png"
              alt="Partners4Cars Logo"
              className="mt-6 w-40 object-contain"
              data-testid="footer-company-logo"
            />
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6 font-mono">
              Navigation
            </h4>
            <div className="space-y-3">
              {[
                { to: '/inventory', label: t('nav.inventory') },
                { to: '/impressions', label: t('nav.impressions') },
                { to: '/about', label: t('nav.about') },
                { to: '/contact', label: t('nav.contact') },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-5">
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6 font-mono">
              {t('contact.info')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <MapPin className="w-4 h-4 text-[#ED2227] shrink-0" strokeWidth={1.5} />
                {t('contact.address')}
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <Mail className="w-4 h-4 text-[#ED2227] shrink-0" strokeWidth={1.5} />
                <a href="mailto:sales@partners4cars.com" className="hover:text-white transition-colors duration-200">
                  {t('contact.emailAddress')}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <Phone className="w-4 h-4 text-[#ED2227] shrink-0" strokeWidth={1.5} />
                <a href="tel:+31625025292" className="hover:text-white transition-colors duration-200">
                  {t('contact.phoneNumber')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        {socialLinks.length > 0 && (
          <div className="border-t border-white/5 mt-12 pt-8" data-testid="footer-social-links">
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6 font-mono">
              Follow Us
            </h4>
            <div className="flex items-center gap-4">
              {socialLinks.map((link) => {
                const IconComponent = ICON_MAP[link.icon?.toLowerCase()] || null;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/5 border border-zinc-800/50 rounded-sm flex items-center justify-center text-zinc-400 hover:text-white hover:bg-[#ED2227] hover:border-[#ED2227] transition-all duration-200"
                    data-testid={`social-link-${link.icon}`}
                    title={link.platform}
                  >
                    {IconComponent ? (
                      <IconComponent className="w-4 h-4" />
                    ) : (
                      <span className="text-[10px] font-bold uppercase">{link.platform?.charAt(0)}</span>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="border-t border-white/5 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 md:pr-[200px]">
          <p className="text-xs text-zinc-600 tracking-widest">
            &copy; {new Date().getFullYear()} Partners4Cars. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/legal/cookies"
              className="text-xs text-zinc-600 hover:text-[#ED2227] tracking-widest uppercase transition-colors"
              data-testid="footer-cookies-link"
            >
              Cookies
            </Link>
            <Link
              to="/legal/privacy-policy"
              className="text-xs text-zinc-600 hover:text-[#ED2227] tracking-widest uppercase transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms-and-conditions"
              className="text-xs text-zinc-600 hover:text-[#ED2227] tracking-widest uppercase transition-colors"
              data-testid="footer-terms-link"
            >
              Terms & Conditions
            </Link>
            <a
              href="https://wa.me/31615254599"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-zinc-600 hover:text-[#ED2227] tracking-widest uppercase transition-colors"
              data-testid="footer-whatsapp-link"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
